// @ts-check
/** @typedef {import('@components/cards/businessview/shared/businessView.types').BusinessViewWorkflowDetailResponse} BusinessViewWorkflowDetailResponse */
import axios from 'axios';

import { useAuthenticatedQuery } from './useAuthenticatedQuery';

import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '../config';
import logError from './metadata/ErrorLogging';

/**
 * Path of the DashboardAPI Business View workflow detail operation, relative to
 * API_BASE_URL. Kept as a single exported helper so the route only has to be
 * changed in one place (see docs/api.yaml -> getProjectWorkflowDetails).
 *
 * @param {string|number} projectId
 * @param {string|number} workflowId
 * @returns {string}
 */
export const buildWorkflowDetailPath = (projectId, workflowId) =>
  `project_workflows/${projectId}/details/${workflowId}`;

/**
 * Marker returned instead of a payload when the API reports that the workflow
 * does not exist for this project. See the 404 handling below.
 */
const NOT_FOUND = { notFound: true };

/**
 * Fetches the current persisted calculation for one Business View workflow, with
 * its linked tests grouped by test type.
 *
*
 * @function useBusinessViewWorkflowDetail
 * @param {string|number|null|undefined} projectId
 * @param {string|number|null|undefined} workflowId
 *
 * @returns {{
 *   detail: BusinessViewWorkflowDetailResponse|null,
 *   notFound: boolean,
 *   isLoading: boolean,
 *   refetch: () => Promise<any>,
 *   error: any
 * }}
 */
export default function useBusinessViewWorkflowDetail(projectId, workflowId) {
  const fetchWorkflowDetail = async (token) => {
    // The query is disabled without both ids, so this cannot run with either missing.
    // Fails here instead of requesting a path with `undefined` in it.
    if (!projectId || !workflowId) {
      throw new Error('useBusinessViewWorkflowDetail was called without a project and workflow id.');
    }

    try {
      const response = await axios.get(`${API_BASE_URL}${buildWorkflowDetailPath(projectId, workflowId)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-functions-key': FUNCTION_KEY,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      // isAxiosError rather than optional chaining: it narrows the caught value, and only a
      // real HTTP 404 should be read as "this flow does not exist".
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return NOT_FOUND;
      }

      logError('Failed to load Business View workflow detail in useBusinessViewWorkflowDetail', error, {
        project_id: projectId,
        workflow_id: workflowId,
      });
      throw error;
    }
  };

  const projectScope = [PROJECT_SCOPE];

  // The response is identified by the project and workflow pair, so both key it.
  const queryKey = ['businessViewWorkflowDetail', projectId, workflowId];

  const isEnabled = !!projectId && !!workflowId;

  const {
    data = null,
    isLoading,
    refetch,
    error,
  } = useAuthenticatedQuery(queryKey, fetchWorkflowDetail, projectScope, {
    enabled: isEnabled,
    staleTime: 60 * 1000, // Data will be cached for 1 minute
  });

  const notFound = data === NOT_FOUND;

  return {
    detail: notFound ? null : data,
    notFound,
    isLoading: isEnabled && isLoading,
    refetch,
    error,
  };
}
